import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CATEGORIES, createPost, updatePost, getMyNickname } from '../api/community';
import NicknameModal from './NicknameModal';
import PostImageUploader from './PostImageUploader';

// Mirrors backend IMAGE_LIMITS in lambda/community/shared.mjs.
// Server is authoritative — these values are for UX only (limit selection,
// hide uploader for free/notice). Server validation rejects oversize regardless.
const IMAGE_LIMITS = { review: 3, qna: 1, free: 0, notice: 0 };

// Form for creating or editing a post. mode='new' or mode='edit'.
// In edit mode, `initialPost` provides current values.
// On success, navigates to /community/post/<id>.

export default function PostWriteForm({ mode, initialPost, defaultCategory }) {
  const { isAdmin, getIdToken } = useAuth();
  const navigate = useNavigate();

  const [category, setCategory] = useState(initialPost?.category || defaultCategory || 'review');
  const [title, setTitle] = useState(initialPost?.title || '');
  const [body, setBody] = useState(initialPost?.body || '');
  const [imageUrls, setImageUrls] = useState(initialPost?.imageUrls || []);
  const [imageToken, setImageToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Fetch idToken once on mount for the uploader. The uploader needs it
  // synchronously when the user picks files; lifting it here avoids a
  // promise chain inside the input handler.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const t = await getIdToken();
        if (active && t) setImageToken(t);
      } catch {}
    })();
    return () => { active = false; };
  }, [getIdToken]);

  const [showNickModal, setShowNickModal] = useState(false);
  const [, setNicknameChecked] = useState(false);

  // On mount (new mode only), check if user has a nickname set.
  useEffect(() => {
    if (mode !== 'new') { setNicknameChecked(true); return; }
    let active = true;
    (async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        const data = await getMyNickname(token);
        if (!active) return;
        if (!data.isCustom) {
          setShowNickModal(true);
        }
        setNicknameChecked(true);
      } catch (err) {
        console.warn('[PostWriteForm] nickname check failed:', err.message);
        if (active) setNicknameChecked(true);
      }
    })();
    return () => { active = false; };
  }, [mode, getIdToken]);

  // Available categories — filter out 'notice' for non-admin in NEW mode.
  // In EDIT mode, allow current category to remain even if non-admin.
  const availableCategories = CATEGORIES.filter(c => {
    if (c.id === 'notice') {
      if (isAdmin) return true;
      if (mode === 'edit' && initialPost?.category === 'notice') return true;
      return false;
    }
    return true;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      setError('제목과 본문을 입력해주세요'); return;
    }
    setSubmitting(true); setError('');
    try {
      const token = await getIdToken();
      if (!token) throw new Error('세션이 만료되었습니다');
      // Trim imageUrls to per-category limit. The server enforces this too,
      // but truncating here gives a friendlier UX when the user changes
      // category after attaching images.
      const allowedImages = imageUrls.slice(0, IMAGE_LIMITS[category] || 0);
      let result;
      if (mode === 'edit') {
        result = await updatePost({
          token,
          postId: initialPost.postId,
          category, title: title.trim(), body: body.trim(),
          imageUrls: allowedImages,
        });
      } else {
        result = await createPost({
          token,
          category, title: title.trim(), body: body.trim(),
          imageUrls: allowedImages,
        });
      }
      navigate(`/community/post/${result.post.postId}`);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <>
      <NicknameModal
        open={showNickModal}
        onClose={() => setShowNickModal(false)}
        onSave={() => setShowNickModal(false)}
      />

      <form onSubmit={submit}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            카테고리
          </label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            style={{
              padding: '10px 14px', borderRadius: 'var(--radius)',
              border: '1px solid var(--border)', background: 'var(--bg)',
              color: 'var(--text)', fontSize: '0.95rem', minWidth: 160,
            }}
          >
            {availableCategories.map(c => (
              <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>
            ))}
          </select>
          <p style={{ margin: '6px 0 0 0', fontSize: '0.78rem', color: 'var(--text-dim)' }}>
            {IMAGE_LIMITS[category] > 0
              ? `사진 최대 ${IMAGE_LIMITS[category]}장 첨부 가능`
              : '이 카테고리는 사진 첨부를 지원하지 않습니다 (텍스트만)'}
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            제목
          </label>
          <input
            className="input"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="제목 (1~100자)"
            maxLength={100}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            본문
          </label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="본문 (1~5000자)"
            maxLength={5000}
            rows={12}
            style={{
              width: '100%', padding: '12px 14px',
              borderRadius: 'var(--radius)', border: '1px solid var(--border)',
              background: 'var(--bg)', color: 'var(--text)',
              fontSize: '0.95rem', lineHeight: 1.6, resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
          <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', color: 'var(--text-dim)', textAlign: 'right' }}>
            {body.length} / 5000
          </p>
        </div>

        {(IMAGE_LIMITS[category] || 0) > 0 && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              사진 첨부
            </label>
            <PostImageUploader
              token={imageToken}
              max={IMAGE_LIMITS[category]}
              value={imageUrls}
              onChange={setImageUrls}
              disabled={!imageToken || submitting}
            />
          </div>
        )}

        {error && (
          <p style={{ color: '#ff5252', fontSize: '0.9rem', marginBottom: 12 }}>❌ {error}</p>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate(-1)}
            style={{ padding: '10px 20px', fontSize: '0.9rem' }}
          >
            취소
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !title.trim() || !body.trim()}
            style={{ padding: '10px 24px', fontSize: '0.9rem' }}
          >
            {submitting ? (mode === 'edit' ? '수정 중...' : '작성 중...') : (mode === 'edit' ? '수정' : '작성')}
          </button>
        </div>
      </form>
    </>
  );
}
